export const INSPECTION_CATEGORY = {
  Laptops: 'Laptops',
  LaptopsDEP: 'Laptops con DEP',
  LaptopsCTO: 'Laptops CTOs',
  Monitors: 'Monitores'
} as const;

export const TOOL_INSPECTION_FIELD_ID = {
  HAS_STICKERS: 1,
  IS_CLEAN: 2,
  INCLUDE_CABLE: 3,
  INCLUDE_CHARGER: 4,
  INCLUDE_BOX: 5,
  TURNS_ON: 6,
  SCREEN_FUNCTIONAL: 7,
  KEYBOARD_FUNCTIONAL: 8,
  LANGUAGE: 9,
  PHOTOS: 10,
  HAS_ORIGINAL_BOX: 11,
  COMMENTS: 12,
  FUNCTIONAL_OS: 13,
  CHARGER_CONDITION: 14,
  VIDEO_CABLE: 15,
  HAS_ORIGINAL_CABLE: 16
} as const;

export const TOOL_INSPECTION_FIELD_DATA_TYPE = {
  BOOLEAN: 'boolean',
  STRING: 'string',
  IMAGE: 'image',
  JSON: 'json'
} as const;

export const TOOL_INSPECTION_IMAGE_SLOTS = {
  FRONT: 'front',
  BACK: 'back',
  SERIAL: 'serial',
  SCREEN: 'screen',
  BOX: 'box',
  KEYBOARD: 'keyboard',
  OPTIONAL_1: 'optional_1',
  OPTIONAL_2: 'optional_2',
  OPTIONAL_3: 'optional_3'
} as const;

export const TOOL_INSPECTION_TYPE_IDS = {
  INITIAL: 1,
  ENTRY: 2,
  CYCLIC: 3,
  EXIT: 4,
  MANUAL: 5
} as const;

export const FUNCTIONAL_OS_OPTIONS = {
  LINUX: 'Linux',
  LINUX_UBUNTU: 'Linux / Ubuntu',
  MACOS: 'MacOS',
  W11_PRO: 'W11 Pro',
  W11: 'W11',
  W10_PRO: 'W10 Pro',
  W10: 'W10',
  NO_OS: 'Sin sistema operativo',
  OTHER: 'Otros'
} as const;

export const LANGUAGE_OPTIONS = {
  SPANISH: 'Español',
  ENGLISH: 'Inglés',
  PORTUGUESE: 'Portugués'
} as const;

export const VIDEO_CABLE_OPTIONS = {
  NONE: 'No incluye',
  VGA: 'VGA',
  HDMI: 'HDMI',
  DISPLAY_PORT: 'DisplayPort',
  MINI_DISPLAYPORT: 'Mini DisplayPort',
  DVI_D: 'DVI-D'
} as const;

export const CHARGER_CONDITIONS = {
  NEW: 'Nuevo',
  LIKE_NEW: 'Usado como nuevo',
  GOOD: 'Usado bueno',
  FAIR: 'Usado regular',
  POOR: 'Usado mal',
  VERY_POOR: 'Usado muy mal',
  NONE: 'No incluye'
} as const;

export type TFunctionalOsOptionValue = typeof FUNCTIONAL_OS_OPTIONS[keyof typeof FUNCTIONAL_OS_OPTIONS];

export const FUNCTIONAL_OS_SELECT_OPTIONS: ReadonlyArray<{
  value: TFunctionalOsOptionValue;
  label: string;
}> = Object.values(FUNCTIONAL_OS_OPTIONS).map(value => ({
  value,
  label: value
}));

export type TLanguageNameValue = typeof LANGUAGE_OPTIONS[keyof typeof LANGUAGE_OPTIONS];

export const LANGUAGE_SELECT_OPTIONS: ReadonlyArray<{
  value: TLanguageNameValue;
  label: string;
}> = Object.values(LANGUAGE_OPTIONS).map(value => ({
  value,
  label: value
}));

export type TVideoCableOptionValue = typeof VIDEO_CABLE_OPTIONS[keyof typeof VIDEO_CABLE_OPTIONS];

export const VIDEO_CABLE_SELECT_OPTIONS: ReadonlyArray<{
  value: TVideoCableOptionValue;
  label: string;
}> = Object.values(VIDEO_CABLE_OPTIONS).map(value => ({
  value,
  label: value
}));

export type TChargerConditionValue = typeof CHARGER_CONDITIONS[keyof typeof CHARGER_CONDITIONS];

export const CHARGER_CONDITION_SELECT_OPTIONS: ReadonlyArray<{
  value: TChargerConditionValue;
  label: string;
}> = Object.values(CHARGER_CONDITIONS).map(value => ({
  value,
  label: value
}));

export type TToolInspectionFieldDataType =
  typeof TOOL_INSPECTION_FIELD_DATA_TYPE[keyof typeof TOOL_INSPECTION_FIELD_DATA_TYPE];

export type TToolInspectionParsedValue = boolean | string | string[] | Record<string, unknown>;

export interface IInitialFieldConfig {
  id: number;
  label: string;
  dataType: TToolInspectionFieldDataType;
  value?: TToolInspectionParsedValue;
}

const laptopInitialFields: Record<number, IInitialFieldConfig> = {
  [TOOL_INSPECTION_FIELD_ID.HAS_STICKERS]: {
    id: TOOL_INSPECTION_FIELD_ID.HAS_STICKERS,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Tiene Stickers',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.IS_CLEAN]: {
    id: TOOL_INSPECTION_FIELD_ID.IS_CLEAN,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Esta limpio',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.INCLUDE_CABLE]: {
    id: TOOL_INSPECTION_FIELD_ID.INCLUDE_CABLE,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Incluye cable',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.HAS_ORIGINAL_CABLE]: {
    id: TOOL_INSPECTION_FIELD_ID.HAS_ORIGINAL_CABLE,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Tiene cable original',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.INCLUDE_CHARGER]: {
    id: TOOL_INSPECTION_FIELD_ID.INCLUDE_CHARGER,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Incluye adaptador',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.INCLUDE_BOX]: {
    id: TOOL_INSPECTION_FIELD_ID.INCLUDE_BOX,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Incluye caja',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.HAS_ORIGINAL_BOX]: {
    id: TOOL_INSPECTION_FIELD_ID.HAS_ORIGINAL_BOX,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Tiene caja original',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.TURNS_ON]: {
    id: TOOL_INSPECTION_FIELD_ID.TURNS_ON,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Enciende',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.SCREEN_FUNCTIONAL]: {
    id: TOOL_INSPECTION_FIELD_ID.SCREEN_FUNCTIONAL,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Pantalla funcional',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.KEYBOARD_FUNCTIONAL]: {
    id: TOOL_INSPECTION_FIELD_ID.KEYBOARD_FUNCTIONAL,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Teclado funcional',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.FUNCTIONAL_OS]: {
    id: TOOL_INSPECTION_FIELD_ID.FUNCTIONAL_OS,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.STRING,
    label: 'Sistema operativo funcional',
    value: FUNCTIONAL_OS_OPTIONS.NO_OS
  },
  [TOOL_INSPECTION_FIELD_ID.LANGUAGE]: {
    id: TOOL_INSPECTION_FIELD_ID.LANGUAGE,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.STRING,
    label: 'Idioma',
    value: LANGUAGE_OPTIONS.SPANISH
  },
  [TOOL_INSPECTION_FIELD_ID.CHARGER_CONDITION]: {
    id: TOOL_INSPECTION_FIELD_ID.CHARGER_CONDITION,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.STRING,
    label: 'Condición cargador',
    value: CHARGER_CONDITIONS.NONE
  },
  [TOOL_INSPECTION_FIELD_ID.COMMENTS]: {
    id: TOOL_INSPECTION_FIELD_ID.COMMENTS,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.STRING,
    label: 'Comentarios'
  }
};

const monitorInitialFields: Record<number, IInitialFieldConfig> = {
  [TOOL_INSPECTION_FIELD_ID.HAS_STICKERS]: {
    id: TOOL_INSPECTION_FIELD_ID.HAS_STICKERS,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Tiene Stickers',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.IS_CLEAN]: {
    id: TOOL_INSPECTION_FIELD_ID.IS_CLEAN,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Esta limpio',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.INCLUDE_CABLE]: {
    id: TOOL_INSPECTION_FIELD_ID.INCLUDE_CABLE,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Incluye cable',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.HAS_ORIGINAL_CABLE]: {
    id: TOOL_INSPECTION_FIELD_ID.HAS_ORIGINAL_CABLE,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Tiene cable original',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.INCLUDE_CHARGER]: {
    id: TOOL_INSPECTION_FIELD_ID.INCLUDE_CHARGER,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Incluye adaptador',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.VIDEO_CABLE]: {
    id: TOOL_INSPECTION_FIELD_ID.VIDEO_CABLE,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.JSON,
    label: 'Incluye cable de video',
    value: [VIDEO_CABLE_OPTIONS.NONE]
  },
  [TOOL_INSPECTION_FIELD_ID.INCLUDE_BOX]: {
    id: TOOL_INSPECTION_FIELD_ID.INCLUDE_BOX,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Incluye caja',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.HAS_ORIGINAL_BOX]: {
    id: TOOL_INSPECTION_FIELD_ID.HAS_ORIGINAL_BOX,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Tiene caja original',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.TURNS_ON]: {
    id: TOOL_INSPECTION_FIELD_ID.TURNS_ON,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Enciende',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.SCREEN_FUNCTIONAL]: {
    id: TOOL_INSPECTION_FIELD_ID.SCREEN_FUNCTIONAL,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN,
    label: 'Pantalla funcional',
    value: false
  },
  [TOOL_INSPECTION_FIELD_ID.CHARGER_CONDITION]: {
    id: TOOL_INSPECTION_FIELD_ID.CHARGER_CONDITION,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.STRING,
    label: 'Condición cargador',
    value: CHARGER_CONDITIONS.NONE
  },
  [TOOL_INSPECTION_FIELD_ID.COMMENTS]: {
    id: TOOL_INSPECTION_FIELD_ID.COMMENTS,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.STRING,
    label: 'Comentarios'
  }
};

export const defaultInitialFields: Record<number, IInitialFieldConfig> = {
  [TOOL_INSPECTION_FIELD_ID.COMMENTS]: {
    id: TOOL_INSPECTION_FIELD_ID.COMMENTS,
    dataType: TOOL_INSPECTION_FIELD_DATA_TYPE.STRING,
    label: 'Comentarios'
  }
};

export const INITIAL_FIELDS_BY_CATEGORY: Record<string, Record<number, IInitialFieldConfig>> = {
  [INSPECTION_CATEGORY.Laptops]: laptopInitialFields,
  [INSPECTION_CATEGORY.LaptopsCTO]: laptopInitialFields,
  [INSPECTION_CATEGORY.LaptopsDEP]: laptopInitialFields,
  [INSPECTION_CATEGORY.Monitors]: monitorInitialFields
};
